import { yupResolver } from '@hookform/resolvers/yup'
import { ControlledTextField } from '@if/mui'
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import AsyncActionButton from '@src/shared/components/AsyncActionButton'
import { reportErrorToConsole } from '@src/shared/services/error-logger'
import { translateError } from '@src/shared/utils/translateError'
import { useBulkAssignTicket } from '@src/tools/error-tracker/api/exceptions-api'
import { BulkAssignTicketRequestDto } from '@src/tools/error-tracker/types/BulkActions'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { bulkAssignTicketSchema, type BulkAssignTicketFormData } from './BulkAssignTicketDialog-schema'

interface BulkAssignTicketDialogProps {
  selectedIds: string[]
  open: boolean
  onSuccess: () => void
  onCancel: () => void
}

const BulkAssignTicketDialog = ({ selectedIds, open, onSuccess, onCancel }: BulkAssignTicketDialogProps) => {
  const { t } = useTranslation()
  const [bulkAssignTicket, isAssigning, assignError, resetAssign] = useBulkAssignTicket()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BulkAssignTicketFormData>({
    resolver: yupResolver(bulkAssignTicketSchema),
    defaultValues: bulkAssignTicketSchema.getDefault(),
  })

  useEffect(() => {
    reset(bulkAssignTicketSchema.getDefault())
    resetAssign()
  }, [open, reset, resetAssign])

  const onSubmit = async (formData: BulkAssignTicketFormData) => {
    try {
      const request: BulkAssignTicketRequestDto = {
        exceptionIds: selectedIds,
        ticketNumber: parseInt(formData.ticketNumber, 10),
      }
      await bulkAssignTicket(request)
      onSuccess()
    } catch (err) {
      reportErrorToConsole(err)
    }
  }

  const handleCancel = () => {
    reset()
    resetAssign()
    onCancel()
  }

  const selectedCount = selectedIds.length

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>{t('dialogs.bulkAssignTicket.title', 'Assign Existing Ticket')}</DialogTitle>
        <DialogContent>
          {assignError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(
                'dialogs.bulkAssignTicket.error',
                'An error occurred while assigning ticket to the selected items. Please try again.',
              )}
            </Alert>
          )}

          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('dialogs.bulkAssignTicket.confirmMessage', {
              defaultValue: 'Assign existing ticket to {{count}} selected {{items}}',
              count: selectedCount,
              items: selectedCount === 1 ? 'item' : 'items',
            })}
          </Typography>

          <ControlledTextField
            name="ticketNumber"
            control={control}
            label={t('dialogs.bulkAssignTicket.ticketNumberLabel', 'Ticket Number')}
            placeholder={t('dialogs.bulkAssignTicket.ticketNumberPlaceholder', 'Enter ticket number...')}
            type="number"
            fullWidth
            required
            disabled={isAssigning}
            helperText={translateError(t, errors.ticketNumber)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <AsyncActionButton variant="outlined" onClick={handleCancel} disabled={isAssigning} type="button">
            {t('common.cancel', 'Cancel')}
          </AsyncActionButton>
          <AsyncActionButton
            variant="contained"
            color="primary"
            isPending={isAssigning}
            disabled={isAssigning || selectedCount === 0}
            type="submit"
          >
            {t('dialogs.bulkAssignTicket.applyButton', 'Assign Ticket to {{count}} {{items}}', {
              count: selectedCount,
              items: selectedCount === 1 ? 'Item' : 'Items',
            })}
          </AsyncActionButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default BulkAssignTicketDialog
